/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */

/**
 * The types for Chart component
 *
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Maxim Fedorov
 * @version 1.21.0
 * @since 1.1.0
 */

import { CrosshairsType, G2 } from 'bizcharts/typings';

import { EChartLabelType } from '../../../pages/PageExpensesAnalyzer/types/pageExpensesAnalyzerTypes';

/*
* -------------------------------------------
*           CHART DATA
* -------------------------------------------
*/

export enum EChartDataKeyEnum {
    checked = 'checked',
    color = 'color',
    name = 'name',
    order = 'order',
    total = 'total',
    x = 'x',
    y = 'y',
}

export interface IChartDataInput {
    [EChartDataKeyEnum.checked]: boolean;
    [EChartDataKeyEnum.order]: number;
    [EChartDataKeyEnum.total]: number;
    [EChartDataKeyEnum.x]: string;
    [EChartDataKeyEnum.y]: {
        [account: string]: number;
    };
}

export interface IChartDataByAccount {
    [account: string]: { [field: string]: number }
}

export interface IChartDataOutput {
    [EChartDataKeyEnum.name]: string;
    [EChartDataKeyEnum.x]: string;
    [EChartDataKeyEnum.y]: number;
}

export type IChartLabels = {
    [key in number | string]: {
        title: string;
        subTitle: string;
        labelType: EChartLabelType;
    };
};

export interface IChartCategoryRange {
    from: string;
    to: string;
}


/*
* -------------------------------------------
*           CHART CONFIGURATION
* -------------------------------------------
*/

export type TChartType = 'intervalStack' | 'line';
export type TChartColumn = 'cat';

interface IChartScale {
    max?: number;
    min?: number;
    range?: [number, number];
    tickInterval?: number;
    type?: TChartColumn;
}

interface IChartAnimate {
    animation: string;
    duration: number;
}

export interface IChartScaleConfiguration {
    [EChartDataKeyEnum.x]: IChartScale;
    [EChartDataKeyEnum.y]: IChartScale;
}

export interface IChartPaddingConfiguration {
    bottom: number;
    left: number;
    right: number;
    top: number;
}

export interface IChartAnimateConfiguration {
    enter?: IChartAnimate;
}

export interface IChartGridConfiguration {
    x: G2.AxisGrid;
    y: G2.AxisGrid;
}

export interface IChartColumnWidthConfiguration {
    columnsCount: number;
    maxWidth: number;
}

export type TChartDataConfiguration = (item: IChartDataInput, account: string, total?: number) => IChartDataOutput;

/*
* -------------------------------------------
*           CHART TOOLTIP
* -------------------------------------------
*/

export interface IChartTooltipItem {
    color: string;
    id: string;
    name: string;
    value: number;
    title: string;
}


export interface IChartTooltipWrapperTemplate {
    account: string;
    currency: string;
    date: string;
    items: IChartTooltipItem[];
    isHideTooltipDate?: boolean;
    mapIdToLabel: IChartLabels;
    title: string;
    total: number;
}

export interface IChartTooltipItemTemplate {
    currency: string;
    index: number;
    item: IChartTooltipItem;
    mapIdToLabel: IChartLabels;
}

export interface IChartTooltipConfiguration {
    isNotFiltered?: boolean;
    render: (props: IChartTooltipWrapperTemplate) => string;
    crosshair: {
        type?: CrosshairsType;
        style?: G2.Styles.background | G2.Styles.line;
    };
}

/*
* -------------------------------------------
*           CHART LEGEND
* -------------------------------------------
*/

export interface IChartLegendConfiguration {
    dataName: string;
    className?: string;
    legendRow: number;
    withSubtitle?: boolean;
}

/*
* -------------------------------------------
*           CHART MAP TYPES
* -------------------------------------------
*/

export type TMapNameToRegion = { [title: string]: string };
export type TMapNameToColor = { [name: string]: string };
export type TMapNameToTranslation = { [name: string]: string };

/*
* -------------------------------------------
*           CHART LOADING
* -------------------------------------------
*/

export interface IChartLoadingConfiguration {
    chartData: IChartDataOutput[];
    height: number;
    legendItems: IChartDataInput[];
    scale: IChartScaleConfiguration;
}

/*
* -------------------------------------------
*           CHART ERROR
* -------------------------------------------
*/

export interface IChartErrorConfiguration {
    legendItems: IChartDataInput[];
}
